import React, { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  PlusIcon,
  TableCellsIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { DifficultyBadge } from "../Difficulty/Difficulty";
import PassageChip from "../Passages/PassageChip";
import { cardClass, focusRing, isApproved, linkButtonClass, Spinner, StatusBadge } from "../../pages/Exams/TestBuilder/ui";
import { NumberBadge, QuestionLine, UnreadableNote } from "./QuestionContent";
import { badgeClass, slotActionsClass, slotAddButtonClass, slotButtonClass, slotRemoveButtonClass } from "./variantUi";
import {
  bankHref,
  hasNoTopic,
  isUnreadable,
  LEVELS,
  placeKey,
  quarterLabel,
  rowIdentity,
  rowPlace,
  rowSubtopics,
  weekLabel,
} from "./variantText";

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);
// content-start: the places keep their own height next to a tall row header.
const slotGridClass = "grid min-w-0 content-start gap-2 sm:grid-cols-2 min-[2200px]:grid-cols-3";
// How many of a week's subtopics show before "+N" (as in the matrix).
const SUBTOPICS_SHOWN = 3;

// The places of a matrix row: per level one per planned question (A, A, …, B, …, C), each holding the variant's
// question or empty; questions over the plan come after their level's places, marked. `key` names a place in this
// read (writes and focus go by it), `id` the same place over saves of the matrix (its element's React key).
export function rowSlots(entry, tqById) {
  const slots = [];
  LEVELS.forEach((level) => {
    const placed = (entry.test_questions?.[level] || []).map((uuid) => tqById.get(uuid)).filter(Boolean).sort(byOrder);
    const planned = entry.planned?.[level] || 0;
    for (let i = 0; i < Math.max(planned, placed.length); i += 1) {
      slots.push({
        key: placeKey(entry.row, level, i),
        id: `${rowIdentity(entry.row)}:${level}:${i}`,
        level,
        tq: placed[i] || null,
        extra: i >= planned,
      });
    }
  });
  return slots;
}

// Consecutive rows of one quarter (the check lists them by quarter, week, then the matrix's order).
function byQuarter(rows) {
  const groups = [];
  rows.forEach((entry) => {
    const quarter = entry.row?.quarter ?? null;
    const last = groups[groups.length - 1];
    if (last && last.quarter === quarter) last.rows.push(entry);
    else groups.push({ quarter, rows: [entry] });
  });
  return groups;
}

// "A 2/2 · B 0/1": what a row has against its plan, per level (green: as planned, amber: short, red: over).
function LevelCounts({ entry }) {
  const [t] = useTranslation();
  const levels = LEVELS.filter((level) => entry.planned?.[level] || entry.actual?.[level]);
  if (!levels.length) return <p className="mt-1 text-xs text-slate-500">{t("variants.row.nothing_planned")}</p>;
  return (
    <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs tabular-nums">
      {levels.map((level) => {
        const planned = entry.planned?.[level] || 0;
        const actual = entry.actual?.[level] || 0;
        const tone = actual === planned ? "text-emerald-700" : actual < planned ? "text-amber-700" : "text-red-700";
        return (
          <span key={level} className={tone} title={t("variants.row.level_counts", { level, actual, planned })}>
            <span className="font-semibold">{level}</span> {actual}/{planned}
          </span>
        );
      })}
    </p>
  );
}

// A week's learning objectives, one a line: the first three and a "+N" that shows the rest (as the matrix lists
// them), so a week of many does not make its row a page tall.
function RowSubtopics({ subtopics }) {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const extra = Math.max(0, subtopics.length - SUBTOPICS_SHOWN);
  return (
    <>
      <ul id={listId} className="space-y-0.5 text-sm text-slate-600">
        {(open ? subtopics : subtopics.slice(0, subtopics.length - extra)).map((subtopic, index) => (
          <li key={subtopic?.uuid || index} className="flex gap-1.5">
            <span className="text-slate-400" aria-hidden="true">
              ›
            </span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{subtopic?.name}</span>
          </li>
        ))}
      </ul>
      {extra > 0 && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={open ? undefined : t("variants.row.more_subtopics", { count: extra })}
          onClick={() => setOpen((value) => !value)}
          className={`${linkButtonClass} -ml-1.5 py-0.5 text-xs max-sm:min-h-11`}
        >
          {open ? t("variants.row.fewer_subtopics") : `+${extra}`}
        </button>
      )}
    </>
  );
}

// "Changed": a place someone else's change touched (the list above the composition names it).
function ChangedBadge() {
  const [t] = useTranslation();
  return <span className={`${badgeClass} bg-amber-50 text-amber-800 ring-amber-600/20`}>{t("variants.changed.mark")}</span>;
}

// A passage the user no longer reads (its question's): it comes without a name.
function UnreadablePassageChip() {
  const [t] = useTranslation();
  return (
    <span className={`${badgeClass} max-w-[12rem] bg-indigo-50 text-indigo-700 ring-indigo-600/20`}>
      <BookOpenIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{t("variants.unreadable.passage")}</span>
    </span>
  );
}

/**
 * One place of the composition. Empty: "Add" (the candidates of its row and level), and the bank's questions of
 * that place when the bank has no other approved one; a place of a row without a topic can't be filled until the
 * matrix names one (no question has it): no "Add" there, its row says why. Filled: the question's number in the
 * variant, a short preview (opens the question), its code, difficulty and passage; "Replace" keeps its number,
 * "Remove" empties it. A passage's question goes only with its whole passage. A question the user no longer reads
 * (moved to a folder not given to them) has no text to preview: the place says so, and it is replaced or removed
 * like any other. `busy`: its write is running; `changed`: someone else's change touched it. The actions get the
 * button pressed last, so the focus can come back to it after the write.
 */
function Slot({ slot, entry, folderUuid, readOnly, busy, flash, changed, keepOrder, onAdd, onReplace, onRemove, onRemovePassage, onView }) {
  const [t] = useTranslation();
  const { level, tq, extra } = slot;

  if (!tq) {
    const noTopic = hasNoTopic(entry.row);
    const free = Math.max(0, (entry.approved?.[level] || 0) - (entry.actual?.[level] || 0));
    const href = !noTopic && free === 0 ? bankHref(folderUuid, entry.row, level) : null;
    return (
      <li
        data-slot={slot.key}
        className={`flex min-w-0 flex-col justify-between gap-2 rounded-xl border border-dashed p-3 ${
          changed ? "border-amber-400 bg-amber-50/70" : "border-slate-300 bg-slate-50/70"
        }`}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <DifficultyBadge level={level} />
          <span className="text-sm font-medium text-slate-500">{t("variants.slot.empty")}</span>
          {changed && <ChangedBadge />}
        </div>
        {href && (
          <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-amber-800">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
            {t("variants.slot.no_free")}
            <a href={href} target="_blank" rel="noopener noreferrer" className={`${linkButtonClass} px-1 py-0 text-xs max-sm:min-h-11`}>
              {t("variants.find_in_bank")}
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </p>
        )}
        {!readOnly && !noTopic && (
          <div className={slotActionsClass}>
            <button
              type="button"
              onClick={(e) => onAdd(entry, level, slot.key, e.currentTarget)}
              disabled={busy}
              aria-label={t("variants.slot.add_aria", { level })}
              className={slotAddButtonClass}
            >
              {busy ? <Spinner className="h-3.5 w-3.5" /> : <PlusIcon className="h-4 w-4" aria-hidden="true" />}
              {t("variants.slot.add")}
            </button>
          </div>
        )}
      </li>
    );
  }

  const question = tq.question || {};
  const passage = question.passage?.uuid ? question.passage : null;
  const kept = keepOrder.has(question.uuid);
  // Its difficulty and status do not come with it either: the place's level stands for the first (the check placed
  // it by its difficulty), and it blocks the confirmation (other_folder) as a question over the plan does.
  const unreadable = isUnreadable(question);
  return (
    <li
      data-slot={slot.key}
      className={`flex min-w-0 flex-col rounded-xl border p-3 transition-colors duration-700 ${
        flash
          ? "border-emerald-300 bg-emerald-50"
          : changed
            ? "border-amber-400 bg-amber-50/60"
            : extra || unreadable
              ? "border-red-200 bg-red-50/40"
              : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <NumberBadge number={tq.order} />
        <DifficultyBadge level={question.difficulty || level} />
        <span className="font-mono text-xs text-slate-500">{question.code}</span>
        {!unreadable && !isApproved(question) && <StatusBadge status={question.status} />}
        {extra && <span className={`${badgeClass} bg-red-50 text-red-700 ring-red-600/20`}>{t("variants.slot.over_plan")}</span>}
        {changed && <ChangedBadge />}
        {kept && (
          <span className="text-slate-500" title={t("variants.slot.keep_order")}>
            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only">{t("variants.slot.keep_order")}</span>
          </span>
        )}
        {passage && (unreadable ? <UnreadablePassageChip /> : <PassageChip passage={passage} className="max-w-[12rem]" />)}
      </div>
      {unreadable ? (
        <div className="mt-1.5">
          <UnreadableNote />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onView(tq)}
          title={t("variants.slot.view")}
          className={`mt-1.5 rounded text-left text-sm text-slate-700 transition-colors duration-200 hover:text-blue-700 max-sm:min-h-11 ${focusRing}`}
        >
          <QuestionLine question={question} />
        </button>
      )}
      {!readOnly && (
        <div className={`mt-auto pt-2 ${slotActionsClass}`}>
          {passage ? (
            <button
              type="button"
              onClick={(e) => onRemovePassage(passage, slot.key, e.currentTarget)}
              disabled={busy}
              className={slotRemoveButtonClass}
              title={t("variants.slot.passage_only")}
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              {t("variants.actions.remove_passage")}
            </button>
          ) : (
            <>
              {!extra && (
                <button
                  type="button"
                  onClick={(e) => onReplace(tq, entry, level, slot.key, e.currentTarget)}
                  disabled={busy}
                  aria-label={t("variants.slot.replace_aria", { number: tq.order })}
                  className={slotButtonClass}
                >
                  <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
                  {t("variants.slot.replace")}
                </button>
              )}
              <button
                type="button"
                onClick={(e) => onRemove(tq, slot.key, e.currentTarget)}
                disabled={busy}
                aria-label={t("variants.slot.remove_aria", { number: tq.order })}
                className={slotRemoveButtonClass}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                {t("variants.slot.remove")}
              </button>
            </>
          )}
          {busy && (
            <span role="status" aria-label={t("variants.saving")}>
              <Spinner className="h-4 w-4" />
            </span>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The variant's questions laid out by its matrix (check.rows): rows grouped by quarter like a plan, each with its
 * week, hours, topic and subtopics (or that its topic is not chosen yet, with the way to the matrix: `matrixHref`;
 * null when the page above already leads there), and its places A, A, …, B, …, C (one per planned question). The variant's questions in no place of the plan
 * (outside the matrix, without a difficulty) are listed after it.
 * `isBusy(key)`: whether that place's write is running; `quarters` the matrix's quarters of the grade (roles);
 * `changes`: the places someone else's change touched ({tqs, emptied}, see variantChanges).
 */
export default function VariantComposition({
  detail,
  quarters,
  folderUuid,
  matrixHref,
  readOnly,
  isBusy,
  flashIds,
  changes,
  onAdd,
  onReplace,
  onRemove,
  onRemovePassage,
  onView,
}) {
  const [t] = useTranslation();
  const rows = useMemo(() => detail.check?.rows || [], [detail]);
  const tqById = useMemo(() => new Map((detail.test_questions || []).map((tq) => [tq.uuid, tq])), [detail]);
  const groups = useMemo(() => byQuarter(rows), [rows]);
  const keepOrder = useMemo(() => new Set(detail.keep_order || []), [detail]);
  // The variant's questions that stand in no place of the plan.
  const outside = useMemo(() => {
    const placed = new Set(rows.flatMap((entry) => LEVELS.flatMap((level) => entry.test_questions?.[level] || [])));
    return (detail.test_questions || []).filter((tq) => !placed.has(tq.uuid)).sort(byOrder);
  }, [rows, detail]);

  const slotBusy = (slot) => {
    const passage = slot.tq?.question?.passage?.uuid;
    return isBusy(slot.key) || (!!slot.tq && isBusy(`tq:${slot.tq.uuid}`)) || (!!passage && isBusy(`passage:${passage}`));
  };
  const slotProps = (slot, entry) => ({
    slot,
    entry,
    folderUuid,
    readOnly,
    busy: slotBusy(slot),
    flash: !!slot.tq && flashIds.has(slot.tq.question?.uuid),
    changed: slot.tq ? !!changes?.tqs.has(slot.tq.uuid) : !!entry.row && !!changes?.emptied.has(`${rowIdentity(entry.row)}:${slot.level}`),
    keepOrder,
    onAdd,
    onReplace,
    onRemove,
    onRemovePassage,
    onView,
  });

  if (!rows.length && !outside.length) {
    return (
      <section className={`${cardClass} flex flex-col items-center gap-3 px-6 py-14 text-center`}>
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <TableCellsIcon className="h-6 w-6" aria-hidden="true" />
        </span>
        <p className="text-sm font-semibold text-slate-900">{t("variants.composition.no_matrix_title")}</p>
        <p className="max-w-md text-sm text-slate-500">{t("variants.composition.no_matrix_text", { grade: detail.grade })}</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="variant-composition-title" className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 id="variant-composition-title" tabIndex={-1} className="text-base font-semibold text-slate-900 outline-none">
          {t("variants.composition.title")}
        </h4>
        <p className="text-xs text-slate-500">{t("variants.composition.hint")}</p>
      </div>

      {groups.map((group) => {
        const info = (quarters || []).find((q) => (q.quarter ?? null) === group.quarter);
        const planned = group.rows.reduce((sum, entry) => sum + LEVELS.reduce((s, level) => s + (entry.planned?.[level] || 0), 0), 0);
        const filled = group.rows.reduce(
          (sum, entry) => sum + LEVELS.reduce((s, level) => s + Math.min(entry.actual?.[level] || 0, entry.planned?.[level] || 0), 0),
          0
        );
        return (
          <section key={group.quarter ?? "none"} aria-label={quarterLabel(t, group.quarter)} className={`${cardClass} overflow-hidden`}>
            <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-100 bg-slate-50/80 px-4 py-2.5">
              <h5 className="text-sm font-semibold text-slate-900">{quarterLabel(t, group.quarter)}</h5>
              {info?.role && (
                <span className={`${badgeClass} bg-white text-slate-600 ring-slate-300`}>{t(`variants.row.role_${info.role}`)}</span>
              )}
              <span className="ml-auto text-xs tabular-nums text-slate-500">
                {t("variants.composition.quarter_stats", { filled, planned })}
                {info?.planned?.points != null && <> · {t("difficulty.points", { count: info.planned.points })}</>}
              </span>
            </header>
            <ol className="divide-y divide-slate-100">
              {group.rows.map((entry) => {
                const slots = rowSlots(entry, tqById);
                const { row } = entry;
                const subtopics = rowSubtopics(row);
                return (
                  <li key={rowIdentity(row)} className="grid min-w-0 gap-3 px-4 py-3 lg:grid-cols-[15rem_minmax(0,1fr)] min-[1920px]:grid-cols-[19rem_minmax(0,1fr)]">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-500">
                        {[row.week ? weekLabel(t, row.week) : null, row.hours != null ? t("variants.row.hours", { count: row.hours }) : null]
                          .filter(Boolean)
                          .join(" · ") || weekLabel(t, null)}
                      </p>
                      {hasNoTopic(row) ? (
                        <>
                          <p className="mt-0.5 text-sm font-medium text-amber-800">{t("variants.row.no_topic")}</p>
                          {/* Once for the week, not in each of its places: none can be filled before it has a topic. */}
                          <p className="text-xs text-slate-500">{t("variants.slot.choose_topic_first")}</p>
                          {matrixHref && (
                            <Link to={matrixHref} className={`${linkButtonClass} -ml-1.5 py-0.5 text-xs max-sm:min-h-11`}>
                              <TableCellsIcon className="h-3.5 w-3.5" aria-hidden="true" />
                              {t("variants.list.open_matrix")}
                            </Link>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="mt-0.5 text-sm font-medium text-slate-900 [overflow-wrap:anywhere]">{row.topic?.name}</p>
                          {subtopics.length > 0 ? (
                            <RowSubtopics subtopics={subtopics} />
                          ) : (
                            <p className="text-xs text-slate-500">{t("variants.row.whole_topic")}</p>
                          )}
                        </>
                      )}
                      <LevelCounts entry={entry} />
                    </div>
                    {slots.length > 0 ? (
                      <ul className={slotGridClass} aria-label={t("variants.row.places", { topic: rowPlace(t, row) })}>
                        {/* Remounted when its question comes or goes: no button carries the focus over to another role
                            (the "Add" just pressed must not become "Remove"). */}
                        {slots.map((slot) => (
                          <Slot key={`${slot.id}:${slot.tq?.uuid || "empty"}`} {...slotProps(slot, entry)} />
                        ))}
                      </ul>
                    ) : (
                      <p className="self-center text-sm text-slate-500">{t("variants.row.nothing_planned")}</p>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}

      {outside.length > 0 && (
        <section aria-labelledby="variant-outside-title" className={`${cardClass} overflow-hidden border-red-200`}>
          <header className="border-b border-red-100 bg-red-50/70 px-4 py-2.5">
            <h5 id="variant-outside-title" className="text-sm font-semibold text-red-800">
              {t("variants.composition.outside_title", { count: outside.length })}
            </h5>
            <p className="mt-0.5 text-xs text-red-700">{t("variants.composition.outside_text")}</p>
          </header>
          <ul className={`${slotGridClass} p-4`}>
            {outside.map((tq) => {
              const slot = { key: `outside:${tq.uuid}`, level: tq.question?.difficulty || null, tq, extra: true };
              return <Slot key={slot.key} {...slotProps(slot, { row: null, planned: {}, actual: {}, approved: {} })} />;
            })}
          </ul>
        </section>
      )}
    </section>
  );
}
